import { useEffect, useId, useRef, useState } from "react";
import { Plus, ChevronLeft, ChevronRight, Upload } from "lucide-react";
import { useI18n } from "../lib/i18n";
import {CropPhotoDialog} from "./CropPhotoDialog";
import {PhotoAttachmentPicker} from "./PhotoAttachmentPicker";
import { ImageLightbox } from "./ImageLightbox";
import { MAX_LISTING_PHOTO_BYTES, LISTING_PHOTO_SIZE_ERROR } from "../lib/listingPhotos";

/**
 * v3.9.6 — required post images (min–max) with a crop-to-fit step.
 * Each selected image is cropped in a locked-ratio frame and exported as a
 * 1200×900 JPEG blob. `value` is the ordered list of cropped blobs; the first
 * is the cover. Business logos and covers reuse the picker/gallery with original image
 * bytes and a single-file contract. Fully self-contained.
 */

export function ImageUploader({
  value, onChange, min = 1, max = 5, multiple = false, allowMultipleSelection = false, compact = false, layout = "grid", example, mediaKind = "photo",
}: { value: File[]; onChange: (files: File[]) => void; min?: number; max?: number; multiple?: boolean; allowMultipleSelection?: boolean; compact?: boolean; layout?: "grid" | "dropzone"; example?: string; mediaKind?: "photo" | "logo" | "cover" }) {
  const { t } = useI18n();
  const fileRef = useRef<HTMLInputElement>(null);
  const fileInputId = useId();
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const [previews, setPreviews] = useState<{ file: File; url: string }[]>([]);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const isLogo = mediaKind === "logo";
  const isCover = mediaKind === "cover";
  const isBranding = isLogo || isCover;
  const brandingLabel = isCover ? "cover photo" : "logo";
  // [posting.media](../../../docs/product/marketplace.md#posting-media): the viewer shares the gallery's order and URLs. File changes
  // dismiss it; effect cleanup releases obsolete URLs on replacement or unmount.
  useEffect(() => {
    const nextPreviews = value.map((file) => ({ file, url: URL.createObjectURL(file) }));
    setPreviews(nextPreviews);
    setActiveIndex(null);
    return () => nextPreviews.forEach(({ url }) => URL.revokeObjectURL(url));
  }, [value]);
  const visiblePreviews = value.map((file) => previews.find((preview) => preview.file === file)).filter((preview): preview is { file: File; url: string } => !!preview);
  const canSelectMultiple = multiple || allowMultipleSelection;
  const useAttachmentRows = layout === "dropzone" && !isBranding;
  const dropzoneDisabled = value.length >= max || cropSrc !== null;
  useEffect(() => () => { if (cropSrc) URL.revokeObjectURL(cropSrc); }, [cropSrc]);

  function pick() { if (value.length < max) fileRef.current?.click(); }
  function onFiles(files: FileList | File[] | null) {
    if (!files?.length) return;
    // Business branding retains original bytes; company image routes cap uploads at 5 MB.
    if (isBranding) {
      if (value.length >= max) return;
      const file = files[0];
      if (!["image/jpeg", "image/png", "image/webp"].includes(file.type) || !file.size || file.size > 5 * 1024 * 1024) {
        setSelectionError(`Choose a JPEG, PNG or WebP ${brandingLabel} up to 5 MB.`);
        return;
      }
      setSelectionError(null);
      onChange([file]);
      return;
    }
    const selected = Array.from(files).filter((file) => file.type.startsWith("image/")).slice(0, canSelectMultiple ? undefined : 1);
    const remaining = Math.max(0, max - value.length - pendingFiles.length - (cropSrc ? 1 : 0));
    const accepted = selected.slice(0, remaining);
    setSelectionError(selected.length > accepted.length ? `You can add up to ${max} photos. The extra files were not added.` : null);
    if (accepted.length === 0) return;
    if (!cropSrc) {
      setCropSrc(URL.createObjectURL(accepted[0]));
      setPendingFiles((queued) => [...queued, ...accepted.slice(1)]);
      return;
    }
    setPendingFiles((queued) => [...queued, ...accepted]);
  }
  function onCropped(blob: Blob) {
    if (blob.size > MAX_LISTING_PHOTO_BYTES) {
      setSelectionError(LISTING_PHOTO_SIZE_ERROR);
      nextCrop();
      return;
    }
    const file = new File([blob], `photo-${value.length + 1}.jpg`, { type: "image/jpeg" });
    onChange([...value, file]);
    nextCrop();
  }
  function nextCrop() {
    const [next, ...rest] = pendingFiles;
    setPendingFiles(rest);
    setCropSrc(next ? URL.createObjectURL(next) : null);
  }
  function cancelCrop() {
    setCropSrc(null);
    setPendingFiles([]);
  }
  function remove(i: number) { onChange(value.filter((_, j) => j !== i)); }
  function move(i: number, dir: -1 | 1) {
    const j = i + dir; if (j < 0 || j >= value.length) return;
    const next = value.slice(); [next[i], next[j]] = [next[j], next[i]]; onChange(next);
  }

  return (
    <div>
      {!useAttachmentRows && <input id={fileInputId} ref={fileRef} type="file" accept={isBranding ? "image/jpeg,image/png,image/webp" : "image/*"} multiple={canSelectMultiple} disabled={layout === "dropzone" && dropzoneDisabled} aria-label={isBranding ? `Choose business ${brandingLabel}` : canSelectMultiple ? "Choose photos" : "Choose photo"} className={layout === "dropzone" ? "sr-only peer" : "hidden"} onChange={(e) => { onFiles(e.currentTarget.files); e.currentTarget.value = ""; }} />}
      {useAttachmentRows ? <PhotoAttachmentPicker value={value} previews={visiblePreviews} max={max} multiple={canSelectMultiple} disabled={cropSrc !== null} onFiles={onFiles} onRemove={remove} onMove={move} onRejected={() => setSelectionError(`You can add up to ${max} photos. The extra files were not added.`)} /> : layout === "dropzone" && <label
        htmlFor={fileInputId}
        className={`flex flex-col items-center gap-2 rounded-xl border-2 border-dashed border-navy-200 bg-navy-50/30 px-4 py-6 text-center transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-navy-500 ${dropzoneDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:border-navy-400 hover:bg-navy-50"}`}
        aria-disabled={dropzoneDisabled}
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => { event.preventDefault(); if (!dropzoneDisabled) onFiles(event.dataTransfer.files); }}
      >
        <Upload className="h-6 w-6 text-navy-500" aria-hidden="true" />
        <span className="font-medium text-navy">{isBranding ? value.length >= max ? `Business ${brandingLabel} added` : `Choose a ${brandingLabel} or drag it here` : value.length >= max ? max === 1 ? "Cover photo added" : "Photo limit reached" : canSelectMultiple ? "Choose photos or drag them here" : "Choose a photo or drag it here"}</span>
        {!dropzoneDisabled && <span className="rounded-lg border border-line bg-white px-3 py-1.5 text-sm font-semibold text-navy">Browse files</span>}
        {example && <span className="max-w-md text-xs font-normal leading-relaxed text-mut">{example}</span>}
        <span className="text-xs text-mut">{isBranding ? `JPEG, PNG or WebP · Up to 5 MB · ${isCover ? "Wide images work best" : "Original proportions"}` : `${max === 1 ? "Marketplace cover" : "First photo is the cover"} · Cropped to 4:3`}</span>
      </label>}
      {!useAttachmentRows && <div className={layout === "dropzone" ? "mt-3 flex flex-wrap gap-2" : `grid grid-cols-2 sm:grid-cols-3 ${compact ? "gap-2" : "gap-3"}`}>
        {value.map((_file, i) => (
          <div key={i} className={`relative ${isCover ? "aspect-[3/1]" : isLogo ? "aspect-square" : "aspect-[4/3]"} rounded-lg overflow-hidden border border-line bg-navy-50 ${layout === "dropzone" ? isCover ? "w-full max-w-xl" : "w-24 sm:w-28" : ""}`}>
            <button type="button" onClick={() => setActiveIndex(i)} disabled={!visiblePreviews[i]} aria-label={isBranding ? `View ${brandingLabel} full size` : `View photo ${i + 1} full size`} className="w-full h-full cursor-zoom-in focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-navy-500">
              <img src={visiblePreviews[i]?.url} alt={isCover ? "Cover photo preview" : isLogo ? "Logo preview" : `photo ${i + 1}`} className={`w-full h-full ${isLogo ? "object-contain" : "object-cover"}`} />
            </button>
            {i === 0 && !isBranding && <span className="pointer-events-none absolute top-1.5 left-1.5 bg-brand-gold text-[#3a2a00] text-[10px] font-extrabold px-1.5 py-0.5 rounded">COVER</span>}
            <button type="button" onClick={() => remove(i)} title="Remove" aria-label={isBranding ? `Remove ${brandingLabel}` : `Remove photo ${i + 1}`} className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-navy/75 text-white grid place-items-center">×</button>
            {value.length > 1 && <div className="absolute bottom-1.5 left-1.5 right-1.5 flex justify-between">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label={`Move photo ${i + 1} earlier`} className="w-6 h-6 rounded bg-white/90 grid place-items-center disabled:opacity-30"><ChevronLeft className="w-3.5 h-3.5" /></button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === value.length - 1} aria-label={`Move photo ${i + 1} later`} className="w-6 h-6 rounded bg-white/90 grid place-items-center disabled:opacity-30"><ChevronRight className="w-3.5 h-3.5" /></button>
            </div>}
          </div>
        ))}
        {layout === "grid" && value.length < max && (
          <button type="button" onClick={pick} className={`${compact && value.length === 0 ? "col-span-2 h-28 sm:col-span-3" : "aspect-[4/3]"} rounded-lg border-2 border-dashed border-[#c7d0de] grid place-items-center text-navy-600 hover:border-navy hover:bg-navy-50 text-sm font-semibold`}>
            <span className="text-center"><Plus className="w-5 h-5 mx-auto mb-1" />{canSelectMultiple ? "Add photos" : "Add photo"}</span>
          </button>
        )}
      </div>}
      {example && layout === "grid" && <p className="mt-2 text-xs leading-relaxed text-mut">{example}</p>}
      <div className="flex flex-wrap items-center justify-between gap-1 mt-2">
        <p className="text-xs text-mut">{value.length}/{max} {isBranding ? brandingLabel : t("photos.countLabel")}{!isBranding && !compact && layout === "grid" && <> · <span className="font-semibold">{t("photos.suggested")} 1200 × 900 px (4:3)</span></>}</p>
        {value.length < min && <p className="text-xs text-brand-red font-semibold">{t("photos.minimum").replace("{min}", String(min))}</p>}
      </div>
      {selectionError && <p role="status" className="text-xs text-brand-red mt-1">{selectionError}</p>}
      {cropSrc && <CropPhotoDialog key={cropSrc} src={cropSrc} queuedCount={pendingFiles.length} onCancel={cancelCrop} onDone={onCropped} />}
      {activeIndex !== null && visiblePreviews[activeIndex] && <ImageLightbox images={visiblePreviews.map(({ url }, index) => ({ src: url, alt: isBranding ? `Business ${brandingLabel}` : `Photo ${index + 1}` }))} initialIndex={activeIndex} onClose={() => setActiveIndex(null)} />}
    </div>
  );
}
